import React, { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, FileImage, FileVideo, FileAudio, FileQuestion, CheckSquare, Square, ExternalLinkIcon, RotateCwIcon, FlagIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { copy } from '../../copy';
import { ConfidenceBand } from './ConfidenceBand';
import { StatusPill, transcriptionTone, reviewTone, edgeTone } from './StatusPill';
import { SensitivityTag } from './SensitivityTag';
type Record = (typeof copy.inventory.records)[number];
interface InventoryTableProps {
  records: Record[];
  selected: string[];
  onToggle: (id: string) => void;
  onToggleAll: () => void;
}
const THRESHOLD = copy.review.confidenceThreshold;
function typeIcon(type: string) {
  switch (type) {
    case 'Doc':
      return <FileText size={16} className="text-red-500" data-id="element-118" />;
    case 'Image':
      return <FileImage size={16} className="text-emerald-500" data-id="element-119" />;
    case 'Audio':
      return <FileAudio size={16} className="text-amber-500" data-id="element-120" />;
    case 'Video':
      return <FileVideo size={16} className="text-purple-500" data-id="element-121" />;
    default:
      return <FileQuestion size={16} className="text-slate-400" data-id="element-122" />;
  }
}
// Flagged / low-confidence first, then by ascending confidence.
function sortRecords(records: Record[]): Record[] {
  return [...records].sort((a, b) => {
    const ca = a.confidence ?? 999;
    const cb = b.confidence ?? 999;
    return ca - cb;
  });
}
export function InventoryTable({
  records,
  selected,
  onToggle,
  onToggleAll
}: InventoryTableProps) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string[]>([]);
  const sorted = sortRecords(records);
  const allSelected = selected.length === records.length && records.length > 0;
  // Find where to draw the explicit 40 divider (first row at/above threshold).
  const dividerIndex = sorted.findIndex(r => (r.confidence ?? 999) >= THRESHOLD);
  const cols = copy.inventory.columns;
  return <div className="overflow-x-auto rounded-3xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a]" data-id="element-123">
      <table className="w-full text-left border-collapse min-w-[1000px]" data-id="element-124">
        <thead data-id="element-125">
          <tr className="border-b border-slate-100 dark:border-[#262626] bg-slate-50/50 dark:bg-[#222]/50" data-id="element-126">
            <th scope="col" className="px-4 py-3 w-10" data-id="element-127">
              <button onClick={onToggleAll} aria-label="Select all" className="text-slate-400 dark:text-[#555]" data-id="element-128">
                {allSelected ? <CheckSquare size={18} className="text-blue-500" data-id="element-129" /> : <Square size={18} data-id="element-130" />}
              </button>
            </th>
            {[cols.filename, cols.type, cols.sizeDuration, cols.transcription, cols.confidence, cols.reviewStatus].map(c => <th key={c} scope="col" className="px-3 py-3 text-[10px] font-semibold text-slate-500 dark:text-[#555] uppercase tracking-wider" data-id="element-131">
                {c}
              </th>)}
            <th scope="col" className="px-3 py-3 text-[10px] font-semibold text-slate-500 dark:text-[#555] uppercase tracking-wider text-right" data-id="element-132">
              {cols.actions}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-[#262626]" data-id="element-133">
          {sorted.map((file, i) => {
          const isExpanded = expanded.includes(file.id);
          const isSelected = selected.includes(file.id);
          const edge = (file as {
            edge?: string;
          }).edge;
          return <Fragment key={file.id} data-id="element-134">
                {i === dividerIndex && dividerIndex > 0 && <tr aria-hidden="true" data-id="element-135">
                    <td colSpan={8} className="p-0" data-id="element-136">
                      <div className="flex items-center gap-2 px-4 py-1.5 bg-amber-50/60 dark:bg-amber-900/10 border-y border-dashed border-amber-300 dark:border-amber-700" data-id="element-137">
                        <span className="h-px flex-1 bg-amber-300 dark:bg-amber-700" data-id="element-138" />
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400" data-id="element-139">
                          {copy.inventory.thresholdDividerLabel}
                        </span>
                        <span className="h-px flex-1 bg-amber-300 dark:bg-amber-700" data-id="element-140" />
                      </div>
                    </td>
                  </tr>}
                <tr className={`group hover:bg-slate-50 dark:hover:bg-[#222] transition-colors ${isSelected ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''}`} data-id="element-141">
                  <td className="px-4 py-3 align-top" data-id="element-142">
                    <button onClick={() => onToggle(file.id)} aria-label={`Select ${file.name}`} className="text-slate-400 dark:text-[#555]" data-id="element-143">
                      {isSelected ? <CheckSquare size={18} className="text-blue-500" data-id="element-144" /> : <Square size={18} data-id="element-145" />}
                    </button>
                  </td>
                  <td className="px-3 py-3 align-top" data-id="element-146">
                    <div className="flex items-start gap-2" data-id="element-147">
                      <span className="mt-0.5 shrink-0" data-id="element-148">
                        {typeIcon(file.type)}
                      </span>
                      <div className="min-w-0" data-id="element-149">
                        <div className="flex items-center gap-2 flex-wrap" data-id="element-150">
                          <span className="font-medium text-sm text-slate-900 dark:text-white" data-id="element-151">
                            {file.name}
                          </span>
                          {file.sensitivity && <SensitivityTag kind={file.sensitivity as 'privileged' | 'sealed' | 'juvenile'} data-id="element-152" />}
                        </div>
                        <button onClick={() => setExpanded(prev => prev.includes(file.id) ? prev.filter(x => x !== file.id) : [...prev, file.id])} className="mt-1 flex items-center gap-1 text-left text-xs text-slate-500 dark:text-[#737373] hover:text-slate-700 dark:hover:text-[#a3a3a3]" data-id="element-153">
                          {isExpanded ? <ChevronDownIcon size={12} className="shrink-0" data-id="element-154" /> : <ChevronRightIcon size={12} className="shrink-0" data-id="element-155" />}
                          <span className={isExpanded ? '' : 'truncate max-w-[280px]'} data-id="element-156">
                            {file.description}
                          </span>
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 align-top text-xs text-slate-600 dark:text-[#a3a3a3]" data-id="element-157">
                    {file.type}
                  </td>
                  <td className="px-3 py-3 align-top text-xs text-slate-500 dark:text-[#a3a3a3] whitespace-nowrap" data-id="element-158">
                    {file.sizeDuration}
                  </td>
                  <td className="px-3 py-3 align-top" data-id="element-159">
                    {edge ? <StatusPill label={copy.review.edge[edge as keyof typeof copy.review.edge] ?? file.transcription} tone={edgeTone(edge)} data-id="element-160" /> : <StatusPill label={file.transcription} tone={transcriptionTone(file.transcription)} data-id="element-161" />}
                  </td>
                  <td className="px-3 py-3 align-top" data-id="element-162">
                    <ConfidenceBand value={file.confidence} compact data-id="element-163" />
                  </td>
                  <td className="px-3 py-3 align-top" data-id="element-164">
                    <StatusPill label={file.reviewStatus} tone={reviewTone(file.reviewStatus)} data-id="element-165" />
                  </td>
                  <td className="px-3 py-3 align-top text-right" data-id="element-166">
                    <div className="flex items-center justify-end gap-1" data-id="element-167">
                      <button onClick={() => navigate('/case/files/workbench')} title={copy.inventory.actions.open} className="p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-white/10 text-slate-500 dark:text-[#a3a3a3]" data-id="element-168">
                        <ExternalLinkIcon size={15} data-id="element-169" />
                      </button>
                      <button title={copy.inventory.actions.rerun} className="p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-white/10 text-slate-500 dark:text-[#a3a3a3]" data-id="element-170">
                        <RotateCwIcon size={15} data-id="element-171" />
                      </button>
                      <button title={copy.inventory.actions.flag} className="p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-white/10 text-amber-500" data-id="element-172">
                        <FlagIcon size={15} data-id="element-173" />
                      </button>
                    </div>
                  </td>
                </tr>
              </Fragment>;
        })}
        </tbody>
      </table>
    </div>;
}